import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { User, LoginCredentials, LoginResponse } from '@/core/types/api'
import { authApi } from '@/modules/auth/api'
import { useAppStore } from '@/core/stores/useAppStore'
import { setLoggingOut, setAccessToken } from '@/core/api/axios'
import { clearAllStoresOnLogout } from '@/core/stores/storeRegistry'
import { logger } from '@/core/utils/logger'
import {
  isUserAdmin,
  createPermissionCache,
  checkPermission,
  checkGlobalPermission
} from '@/core/permissions/checker'
import { i18n } from '@/locales'

/**
 * Zajišťuje správu uživatelského profilu
 */
function createEmptyUser(): User {
  return {
    id: '',
    email: '',
    name: '',
    roles: [],
    permissions: [],
  }
}

/**
 * Sloučí data uživatele a zajistí správnou strukturu
 */
function mergeUserData(currentUser: User | null, newUserData: Partial<User>): User {
  const base = currentUser || createEmptyUser()

  return {
    ...base,
    ...newUserData,
    roles: newUserData.roles || base.roles || [],
    permissions: newUserData.permissions || base.permissions || [],
  }
}

/**
 * Clear all store data on logout to prevent showing stale cached data.
 * Uses store registry for safe iteration (no Pinia internals access).
 */
function clearAllStores() {
  clearAllStoresOnLogout()
  localStorage.removeItem('currentProjectId')
}

/**
 * Store pro správu autentizace
 *
 * Security Model:
 * - Access token is stored in memory (not accessible from JavaScript or localStorage)
 * - Refresh token is stored in HTTP-only cookie (not accessible from JavaScript)
 * - Only user data and current project ID are persisted in localStorage
 * - Authentication is verified by checking user existence (tokens in HTTP-only cookies)
 *
 * @see {@link https://owasp.org/www-community/HttpOnly | OWASP HttpOnly Documentation}
 */
export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const appStore = useAppStore()

  const user = useStorage<User | null>('auth.user', null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const isAuthReady = ref(false)

  const isAuthenticated = computed(() => !!user.value)

  // Kontrola jestli je uživatel admin
  const isAdmin = computed(() => isUserAdmin(user.value))

  /**
   * Cache uživatelských oprávnění jako Set pro rychlé O(1) lookup
   * Místo array.includes() (O(n)) používáme Set.has() (O(1))
   */
  const userPermissionsCache = computed(() => createPermissionCache(user.value?.permissions))

  /**
   * Optimalizovaná centrální metoda pro kontrolu všech oprávnění v aplikaci
   * Používá Set cache pro O(1) lookup místo O(n) array.includes()
   *
   * @param permission Požadované oprávnění
   * @param projectPermissions Volitelně projektová oprávnění pro kontrolu
   * @returns Boolean indikující, zda má uživatel dané oprávnění
   */
  const hasPermission = (permission: string, projectPermissions?: string[] | null) => {
    return checkPermission(user.value, permission, projectPermissions)
  }

  /**
   * Rychlá kontrola jestli má uživatel globální oprávnění (bez projektu)
   * @param permission Požadované oprávnění
   * @returns Boolean indikující, zda má uživatel globální oprávnění
   */
  const hasGlobalPermission = (permission: string): boolean => {
    return checkGlobalPermission(user.value, permission)
  }

  /**
   * Nastaví autentizační data po přihlášení nebo obnově tokenu
   */
  function setAuthData(authData: LoginResponse) {
    // Store access token in memory for Authorization header (cross-domain auth)
    if (authData.token) {
      setAccessToken(authData.token)
    }

    if (authData.user) {
      user.value = mergeUserData(user.value, authData.user)
    }

    // Mark auth as ready after setting auth data
    isAuthReady.value = true
  }

  /**
   * Přihlášení uživatele
   */
  async function login(credentials: LoginCredentials) {
    try {
      loading.value = true
      error.value = null

      const authData = await authApi.login(credentials)
      setAuthData(authData)

      // Pokud v odpovědi není uživatel, načteme jeho profil
      if (!authData.user) {
        await refreshUser()
      }

      appStore.addNotification(i18n.global.t('auth.login.success'), 'success')
      router.push({ name: 'dashboard' })
      return authData
    } catch (err: unknown) {
      logger.error('Login failed:', err)
      error.value = err instanceof Error ? err.message : i18n.global.t('auth.login.failed')
      appStore.addNotification(
        i18n.global.t('auth.login.failed') + ': ' + (err instanceof Error ? err.message : i18n.global.t('auth.login.invalidCredentials')),
        'error'
      )
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Obnova tokenu
   */
  async function refreshTokens() {
    try {
      const authData = await authApi.refreshToken()
      setAuthData(authData) // This also sets isAuthReady = true

      // Pokud v odpovědi není uživatel, načteme jeho profil
      if (!authData.user) {
        await refreshUser()
      }

      return authData
    } catch (err: unknown) {
      logger.error('Token refresh failed:', err)
      isAuthReady.value = true // Mark as ready so UI can proceed
      // Don't call logout() here - it may hang waiting for API
      // Just clear local state and let caller handle redirect
      setAccessToken(null)
      user.value = null
      throw err
    }
  }

  /**
   * Aktualizace uživatelského profilu
   */
  async function refreshUser() {
    try {
      const userProfile = await authApi.getProfile()
      if (userProfile) {
        user.value = mergeUserData(user.value, userProfile)
      }
      return user.value
    } catch (err: unknown) {
      logger.error('Error refreshing user profile:', err)
      throw err
    }
  }

  /**
   * Odhlášení uživatele
   */
  async function logout() {
    // Set logout state to prevent further API requests
    setLoggingOut(true)

    // Invalidate refresh token on server (fire and forget - don't wait)
    try {
      await authApi.logout()
    } catch {
      // Ignore errors - logout should always succeed locally
      logger.warn('Server logout failed, continuing with local logout')
    }

    // Clear access token from memory
    setAccessToken(null)

    // Clear user data
    user.value = null

    // Reset auth ready state
    isAuthReady.value = false

    // Clear all other stores to prevent stale data
    clearAllStores()

    appStore.addNotification(i18n.global.t('auth.logout.success'), 'info')

    // Reset logout state after redirect
    setLoggingOut(false)

    // Wait for router to be ready before redirecting (important on page refresh)
    await router.isReady()
    router.push({ name: 'login' })
  }

  return {
    user,
    loading,
    error,
    isAuthenticated,
    isAuthReady,
    isAdmin,
    userPermissionsCache,
    hasPermission,
    hasGlobalPermission,
    login,
    refreshTokens,
    refreshUser,
    logout,
  }
})
